class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // Lets CSS hide the Gorgias chat launcher, which otherwise overlaps the checkout button.
    document.body.classList.add('cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // Clear any empty-cart state left over from an empty drawer (i.e. the first add).
    // We do NOT full re-render (see below), so the empty-state markup and the disabled
    // checkout button won't get replaced on their own - clean them up explicitly.
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    const emptyBlock = this.querySelector('.drawer__inner-empty');
    if (emptyBlock) emptyBlock.remove();

    // Surgical re-render so Discount Ninja's live widgets survive an add-to-cart.
    // Re-rendering the whole #CartDrawer (or even detaching + re-attaching DN's nodes)
    // makes DN discard its promotion summary / promo-code field and fall back to its
    // "promotion summary unavailable" state, so the discount area vanishes until a full
    // page refresh. Instead we only swap the line items and update the fallback subtotal
    // value, and never touch the [data-la-dn-*] placeholder nodes. DN refreshes its own
    // widgets in place when it detects the /cart/add change.
    const newCartDrawer = new DOMParser()
      .parseFromString(parsedState.sections['cart-drawer'], 'text/html')
      .querySelector('#CartDrawer');

    if (newCartDrawer) {
      const liveItems = this.querySelector('cart-drawer-items');
      const newItems = newCartDrawer.querySelector('cart-drawer-items');
      if (liveItems && newItems) liveItems.replaceWith(newItems);

      const liveSubtotal = this.querySelector(
        '.limoniapps-discountninja-cart-subtotal-price, .totals__total-value'
      );
      const newSubtotal = newCartDrawer.querySelector(
        '.limoniapps-discountninja-cart-subtotal-price, .totals__total-value'
      );
      if (liveSubtotal && newSubtotal) liveSubtotal.textContent = newSubtotal.textContent;
    }

    // Re-enable the checkout button (it is rendered disabled while the cart is empty).
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) checkoutButton.removeAttribute('disabled');

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
      document.dispatchEvent(new CustomEvent('discount-ninja:cart-items-changed'));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      // 'cart-icon-bubble' omitted: see cart.js. The V2 header owns a custom icon on
      // #cart-icon-bubble; the header count is updated via the cartUpdate pub/sub instead.
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      // 'cart-icon-bubble' omitted: see cart.js. Avoids overwriting the V2 header's
      // custom cart icon; the header count is updated via the cartUpdate pub/sub instead.
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
